import { Env } from '@env';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import React from 'react';
import { View } from 'react-native';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import type { AdditionalDriverPayment } from '@/api/reservations/use-add-additional-driver';
import { formatNumber, rgbaToHex } from '@/core';
import { Button, showErrorMessage } from '@/ui';

const stripePromise = loadStripe(Env.STRIPE_PUBLISHABLE_KEY);

export function AdditionalDriverPaymentForm({
  payment,
  onSucceeded,
  onFailed,
}: {
  payment: AdditionalDriverPayment;
  payer?: {
    name?: string;
    phone?: string;
    email?: string;
  };
  onSucceeded: () => void;
  onFailed: () => void;
}) {
  const { theme } = useStyles();

  return (
    <Elements
      stripe={stripePromise}
      options={{
        locale: 'en',
        clientSecret: payment.stripeSecret,
        ...(payment.customerSessionSecret
          ? { customerSessionClientSecret: payment.customerSessionSecret }
          : {}),
        appearance: {
          variables: {
            colorBackground: rgbaToHex(theme.colors.background_2),
          },
          rules: {
            '.Tab': {
              border: 'none',
            },
          },
          theme: UnistylesRuntime.themeName === 'dark' ? 'night' : 'stripe',
        },
      }}
    >
      <AdditionalDriverStripePayment
        payment={payment}
        onSucceeded={onSucceeded}
        onFailed={onFailed}
      />
    </Elements>
  );
}

function AdditionalDriverStripePayment({
  payment,
  onSucceeded,
  onFailed,
}: {
  payment: AdditionalDriverPayment;
  onSucceeded: () => void;
  onFailed: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isFormValid, setIsFormValid] = React.useState(false);
  const [isPaying, setIsPaying] = React.useState(false);
  const handlePayment = async () => {
    if (!stripe || !elements || isPaying) return;

    setIsPaying(true);

    // Validation errors are shown inline by Stripe, so keep the form open.
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setIsPaying(false);
      return;
    }

    const { error } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    setIsPaying(false);

    if (error) {
      onFailed();
      showErrorMessage('Payment failed. Please try again.');
      return;
    }

    onSucceeded();
  };

  return (
    <View style={{ gap: 16 }}>
      <PaymentElement
        onChange={(event) => {
          setIsFormValid(event.complete);
        }}
        options={{
          business: { name: 'Eon' },
          layout: {
            type: 'accordion',
            radios: true,
            spacedAccordionItems: false,
          },
        }}
      />

      <Button
        text={`Pay $${formatNumber(payment.amount)}`}
        type="primary"
        onPress={handlePayment}
        disabled={!isFormValid || isPaying}
      />
    </View>
  );
}
